/**
 * OfferBulkDownloadTrigger
 *
 * Renders the bulk-download trigger for the offer detail surface.
 * Receives all offer unit IDs from the parent (route level) and
 * IGNORES any row-level selection in the offer's unit table (design decision D-7).
 *
 * The parent route file calls useBulkDownloadSpecs() and passes the resulting
 * callbacks + state as props. This component is purely presentational.
 *
 * Covers REQ-9.4 / Scenario: Offer detail trigger downloads specs for all offer units.
 */
import * as React from 'react';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '@ooh/ui/components/tooltip';
import { Button } from '@ooh/ui/components/button';
import { IconButtonWithTooltip } from '@ooh/ui/components/icon-button-with-tooltip';
import type { BulkDownloadManifestResponse } from '@ooh/contracts';
import { BULK_DOWNLOAD_MAX_UNITS } from '@ooh/contracts';
import { BulkDownloadConfirmDialog } from '../media-units/components/bulk-download-confirm-dialog';

interface DownloadTriggerVariantProps {
  isDisabled: boolean;
  tooltipText: string;
  labelText: string;
  onStart: () => void;
}

function IconOnlyDownloadTrigger({
  isDisabled,
  tooltipText,
  labelText,
  onStart,
}: DownloadTriggerVariantProps) {
  return (
    <IconButtonWithTooltip
      icon={Download}
      tooltip={isDisabled ? tooltipText : labelText}
      disabled={isDisabled}
      onClick={onStart}
      testId="offer-bulk-download-trigger"
    />
  );
}

function TextDownloadTrigger({
  isDisabled,
  tooltipText,
  labelText,
  onStart,
}: DownloadTriggerVariantProps) {
  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* span wrapper required: disabled buttons don't fire pointer events */}
          <span tabIndex={isDisabled ? 0 : undefined}>
            <Button
              data-testid="offer-bulk-download-trigger"
              disabled={isDisabled}
              size="sm"
              variant="outline"
              onClick={onStart}
            >
              {labelText}
            </Button>
          </span>
        </TooltipTrigger>
        {isDisabled ? <TooltipContent>{tooltipText}</TooltipContent> : null}
      </Tooltip>
    </TooltipProvider>
  );
}

interface OfferBulkDownloadTriggerProps {
  /** All media unit IDs belonging to the offer (row selection is ignored per D-7) */
  unitIds: number[];
  /** Whether the current user has the media_unit.view permission */
  canDownloadSpecs: boolean;
  /** start() from useBulkDownloadSpecs() called at route level */
  start: (ids: number[]) => Promise<void>;
  /** confirmDownload() from useBulkDownloadSpecs() */
  confirmDownload: () => Promise<void>;
  /** cancelDownload() from useBulkDownloadSpecs() */
  cancelDownload: () => void;
  /** pendingManifest from useBulkDownloadSpecs() — null when not awaiting confirm */
  pendingManifest: BulkDownloadManifestResponse | null;
  /**
   * Renders an icon-only `IconButtonWithTooltip` (offer-table-chrome dense action
   * row) instead of the default text `Button`. Same testid either way.
   */
  iconOnly?: boolean;
}

export function OfferBulkDownloadTrigger({
  unitIds,
  canDownloadSpecs,
  start,
  confirmDownload,
  cancelDownload,
  pendingManifest,
  iconOnly = false,
}: OfferBulkDownloadTriggerProps) {
  const { t } = useTranslation('inventory');

  if (!canDownloadSpecs) return null;

  const unitCount = unitIds.length;
  const isDisabled = unitCount === 0 || unitCount > BULK_DOWNLOAD_MAX_UNITS;

  const tooltipText =
    unitCount === 0
      ? t('bulkDownload.trigger.disabledEmpty')
      : t('bulkDownload.trigger.disabledCap');
  const labelText = t('bulkDownload.trigger.label');
  const onStart = () => void start(unitIds);

  const Trigger = iconOnly ? IconOnlyDownloadTrigger : TextDownloadTrigger;

  return (
    <>
      <Trigger
        isDisabled={isDisabled}
        tooltipText={tooltipText}
        labelText={labelText}
        onStart={onStart}
      />

      {pendingManifest ? (
        <BulkDownloadConfirmDialog
          manifest={pendingManifest}
          totalSelectedUnits={unitCount}
          onConfirm={() => void confirmDownload()}
          onCancel={cancelDownload}
        />
      ) : null}
    </>
  );
}
